import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cursosAPI, categoriasAPI, empresasAPI, instrutoresAPI, modulosAPI } from '../services/api';
import api from '../services/api';
import { Award, FileImage } from 'lucide-react';
import { AppSidebar } from '../components/AppSidebar';

function CriarCurso() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria_id: '',
    empresa_id: '',
    instrutor_id: '',
    template_certificado_id: '',
    capa: null
  });
  const [categorias, setCategorias] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [instrutores, setInstrutores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modulos, setModulos] = useState([{ nome: '', descricao: '', ordem: 1 }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [categoriasRes, empresasRes, instrutoresRes, templatesRes] = await Promise.all([
        categoriasAPI.listar(),
        empresasAPI.listar(),
        instrutoresAPI.listar(),
        api.get('/admin/templates')
      ]);
      setCategorias(categoriasRes.data.categorias || []);
      setEmpresas(empresasRes.data.empresas || []);
      setInstrutores(instrutoresRes.data.instrutores || []);
      
      // Filtrar apenas templates ativos
      const templatesAtivos = templatesRes.data.templates?.filter(t => t.ativo) || [];
      setTemplates(templatesAtivos);
    } catch (err) {
      setError('Erro ao carregar dados');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      capa: e.target.files[0]
    }));
  };

  const adicionarModulo = () => {
    setModulos(prev => [
      ...prev,
      { nome: '', descricao: '', ordem: prev.length + 1 }
    ]);
  };

  const removerModulo = (index) => {
    if (modulos.length > 1) {
      setModulos(prev => prev.filter((_, i) => i !== index));
    }
  };

  const atualizarModulo = (index, field, value) => {
    setModulos(prev => prev.map((modulo, i) => 
      i === index ? { ...modulo, [field]: value } : modulo
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let capaFileName = '';
      
      // Upload da capa primeiro, se houver
      if (formData.capa) {
        const capaFormData = new FormData();
        capaFormData.append('capa', formData.capa);
        const capaResponse = await cursosAPI.uploadCapa(capaFormData);
        capaFileName = capaResponse.data.arquivo;
      }

      // Criar curso
      const cursoData = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        categoria_id: formData.categoria_id || null,
        empresa_id: formData.empresa_id || null,
        instrutor_id: formData.instrutor_id || null,
        template_certificado_id: formData.template_certificado_id || null,
        capa: capaFileName
      };

      const response = await cursosAPI.criar(cursoData);
      const cursoId = response.data.curso.id;

      // Criar módulos
      if (modulos.length > 0 && modulos[0].nome) {
        for (const modulo of modulos) {
          if (modulo.nome.trim()) {
            await modulosAPI.criar({
              nome: modulo.nome,
              descricao: modulo.descricao,
              curso_id: cursoId,
              ordem: modulo.ordem
            });
          }
        }
      }

      setSuccess('Curso criado com sucesso!');
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate(`/curso/${cursoId}`);
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.erro || 'Erro ao criar curso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppSidebar />
      <div className="ml-64 min-h-screen">
        <main className="relative">
          <div className="container mx-auto px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Criar Novo Curso</h1>
                <p className="text-slate-400">Configure seu curso, adicione módulos e organize o conteúdo</p>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
                <div className="p-6">
                  {error && (
                    <div className="mb-6 p-4 bg-red-600/10 border border-red-600/20 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="mb-6 p-4 bg-green-600/10 border border-green-600/20 rounded-lg text-green-400 text-sm">
                      {success}
                    </div>
                  )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Informações Básicas */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-white">Informações Básicas</h3>
                    
                    <div>
                      <label htmlFor="titulo" className="block text-sm font-medium text-white mb-2">
                        Título do Curso *
                      </label>
                      <input
                        type="text"
                        id="titulo"
                        name="titulo"
                        value={formData.titulo}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                        placeholder="Digite o título do curso"
                      />
                    </div>

                    <div>
                      <label htmlFor="descricao" className="block text-sm font-medium text-white mb-2">
                        Descrição
                      </label>
                      <textarea
                        id="descricao"
                        name="descricao"
                        rows={4}
                        value={formData.descricao}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors resize-none"
                        placeholder="Descreva o conteúdo e objetivos do curso"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label htmlFor="categoria_id" className="block text-sm font-medium text-white mb-2">
                          Categoria
                        </label>
                        <select
                          id="categoria_id"
                          name="categoria_id"
                          value={formData.categoria_id}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                        >
                          <option value="">Selecione uma categoria</option>
                          {categorias.map(categoria => (
                            <option key={categoria.id} value={categoria.id}>
                              {categoria.nome}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="empresa_id" className="block text-sm font-medium text-white mb-2">
                          Empresa
                        </label>
                        <select
                          id="empresa_id"
                          name="empresa_id"
                          value={formData.empresa_id}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                        >
                          <option value="">Selecione uma empresa</option>
                          {empresas.map(empresa => (
                            <option key={empresa.id} value={empresa.id}>
                              {empresa.nome}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="instrutor_id" className="block text-sm font-medium text-white mb-2">
                          Instrutor
                        </label>
                        <select
                          id="instrutor_id"
                          name="instrutor_id"
                          value={formData.instrutor_id}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                        >
                          <option value="">Selecione um instrutor</option>
                          {instrutores.map(instrutor => (
                            <option key={instrutor.id} value={instrutor.id}>
                              {instrutor.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="capa" className="block text-sm font-medium text-white mb-2">
                        Capa do Curso
                      </label>
                      <input
                        type="file"
                        id="capa"
                        name="capa"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-600 file:text-white hover:file:bg-red-700 file:cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                      />
                      <p className="text-slate-400 text-xs mt-2">
                        Formatos aceitos: JPG, PNG, GIF (máx. 16MB)
                      </p>
                    </div>
                  </div>

                  {/* Seleção de Template de Certificado */}
                  <div className="space-y-6 border-t border-slate-800/50 pt-8">
                    <div className="flex items-center gap-3">
                      <Award className="w-6 h-6 text-yellow-400" />
                      <h3 className="text-xl font-semibold text-white">Template de Certificado</h3>
                    </div>
                    <p className="text-slate-400 text-sm">
                      Selecione o modelo que será usado para gerar os certificados deste curso (opcional)
                    </p>

                    {templates.length === 0 ? (
                      <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-6 text-center">
                        <FileImage className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                        <h4 className="text-white font-medium mb-2">Nenhum template disponível</h4>
                        <p className="text-slate-400 text-sm mb-4">
                          Crie templates de certificados no painel administrativo primeiro
                        </p>
                        <button
                          type="button"
                          onClick={() => window.open('/admin/templates', '_blank')}
                          className="px-4 py-2 bg-yellow-600/20 text-yellow-400 border border-yellow-600/30 rounded-lg hover:bg-yellow-600/30 transition-colors text-sm"
                        >
                          Ir para Templates
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Seleção por Select */}
                        <div>
                          <select
                            id="template_certificado_id"
                            name="template_certificado_id"
                            value={formData.template_certificado_id}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                          >
                            <option value="">Nenhum template (usar padrão)</option>
                            {templates.map(template => (
                              <option key={template.id} value={template.id}>
                                {template.nome} ({template.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Preview Visual dos Templates */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* Template Padrão */}
                          <div 
                            className={`cursor-pointer border-2 rounded-lg p-3 transition-all duration-200 ${
                              formData.template_certificado_id === '' 
                                ? 'border-red-500 bg-red-600/10' 
                                : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                            }`}
                            onClick={() => setFormData(prev => ({ ...prev, template_certificado_id: '' }))}
                          >
                            <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 rounded flex items-center justify-center mb-2">
                              <div className="text-center">
                                <Award className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                                <p className="text-slate-400 text-xs">Template Padrão</p>
                              </div>
                            </div>
                            <div className="text-center">
                              <h4 className="text-white text-sm font-medium">Template Padrão</h4>
                              <p className="text-slate-400 text-xs">Sistema usará modelo básico</p>
                            </div>
                          </div>

                          {/* Templates Personalizados */}
                          {templates.map((template) => (
                            <div 
                              key={template.id}
                              className={`cursor-pointer border-2 rounded-lg p-3 transition-all duration-200 ${
                                formData.template_certificado_id === template.id.toString() 
                                  ? 'border-red-500 bg-red-600/10' 
                                  : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                              }`}
                              onClick={() => setFormData(prev => ({ ...prev, template_certificado_id: template.id.toString() }))}
                            >
                              <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 rounded flex items-center justify-center mb-2 relative">
                                {template.thumbnail ? (
                                  <img 
                                    src={`http://localhost:5000/uploads/${template.thumbnail}`}
                                    alt={template.nome}
                                    className="w-full h-full object-cover rounded"
                                  />
                                ) : (
                                  <div className="text-center">
                                    <FileImage className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                                    <p className="text-slate-400 text-xs">Sem preview</p>
                                  </div>
                                )}
                                
                                {/* Badge de Orientação */}
                                <div className="absolute top-1 right-1">
                                  <span className={`px-2 py-1 text-xs rounded ${
                                    template.orientation === 'horizontal' 
                                      ? 'bg-blue-600/80 text-white' 
                                      : 'bg-purple-600/80 text-white'
                                  }`}>
                                    {template.orientation === 'horizontal' ? 'H' : 'V'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-center">
                                <h4 className="text-white text-sm font-medium truncate" title={template.nome}>
                                  {template.nome}
                                </h4>
                                <p className="text-slate-400 text-xs">
                                  {template.width} × {template.height}px
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Módulos do Curso */}
                  <div className="space-y-6 border-t border-slate-800/50 pt-8">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Módulos do Curso</h3>
                      <button
                        type="button"
                        onClick={adicionarModulo}
                        className="px-4 py-2 bg-red-600/20 text-red-400 border border-red-600/30 rounded-lg hover:bg-red-600/30 transition-colors text-sm"
                      >
                        + Adicionar Módulo
                      </button>
                    </div>

                    <div className="space-y-4">
                      {modulos.map((modulo, index) => (
                        <div key={index} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/30">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-medium text-white">Módulo {index + 1}</h4>
                            {modulos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removerModulo(index)}
                                className="text-red-400 hover:text-red-300 transition-colors text-sm"
                              >
                                Remover
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-white mb-2">
                                Nome do Módulo *
                              </label>
                              <input
                                type="text"
                                value={modulo.nome}
                                onChange={(e) => atualizarModulo(index, 'nome', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                                placeholder="Ex: Introdução ao React"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-white mb-2">
                                Descrição
                              </label>
                              <input
                                type="text"
                                value={modulo.descricao}
                                onChange={(e) => atualizarModulo(index, 'descricao', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                                placeholder="Descrição opcional do módulo"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-slate-800/50">
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="flex-1 px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800/50 transition-colors"
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block"></div>
                          Criando...
                        </>
                      ) : (
                        'Criar Curso'
                      )}
                    </button>
                  </div>
                </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default CriarCurso; 